/**
 * RH-01 Burstiness (INV-CH-RH-01, ENGINE-SPEC §3.5, §4; changes C-RH-POOL, C-RH-DAMP).
 *
 * The seed took the CV of sentence length over the whole document, so a document whose sections
 * differ in mean sentence length looked "bursty" even when every section is metronomic. Here the
 * CV is pooled WITHIN the joint segments:
 *
 *     CV = sqrt(Σ_s SS_s / Σ_s (n_s − 1)) / pooled mean · (1 + 1/(4N))
 *
 * (N = total sentences; sample SD, small-sample factor). Run-on units (> 120 words, §2.6 R12) are
 * excluded. Between-segment variation is reported separately (features.betweenSegmentCv).
 * The interval is a stratified paragraph-block bootstrap (helpers.runDispersion).
 */
import { derivePrior } from './framework.ts';
import {
	type DispAcc,
	type DispItems,
	fmt,
	pooledCv,
	rhythmSentenceLengths,
	runDispersion,
	sentenceItems,
} from './helpers.ts';
import type { ChannelDef, ChannelImpl, Ramp, RunContext } from './types.ts';

const RAMP: Ramp = [
	[0.2, 92],
	[0.3, 80],
	[0.4, 62],
	[0.5, 45],
	[0.62, 28],
	[0.8, 12],
];

export const rh01Def: ChannelDef = {
	id: 'RH-01',
	name: 'Burstiness',
	family: 'RHYTHM',
	axis: 'authorship',
	registers: ['prose'],
	direction: -1,
	oneWay: false,
	estimator: 'distributional',
	features: [{ id: 'cv', label: 'Pooled within-segment CV of sentence length' }],
	minExposure: 5,
	exposureUnit: 'sentences',
	lang: 'agnostic',
	ramp: RAMP,
	unitScale: 1,
	unitLabel: 'CV of sentence length',
	prior: derivePrior(RAMP, 1, 'sentences', 1.2),
	weight: 1.7,
	nd: 1,
	changes: ['C-RH-POOL', 'C-RH-DAMP'],
};

function run(ctx: RunContext) {
	const { len, para } = rhythmSentenceLengths(ctx.doc);
	let mn = Number.POSITIVE_INFINITY;
	let mx = 0;
	let s1 = 0;
	let s2 = 0;
	for (const x of len) {
		mn = Math.min(mn, x);
		mx = Math.max(mx, x);
		s1 += x;
		s2 += x * x;
	}
	const N = len.length;
	const meanAll = N ? s1 / N : 0;
	const cvWhole = meanAll > 0 ? Math.sqrt(Math.max(0, s2 / N - meanAll ** 2)) / meanAll : 0;
	return runDispersion(ctx, {
		def: rh01Def,
		salt: 0x52480001,
		analyticFactor: 1,
		build: (segOf): { items: DispItems; exposure: number } => ({
			items: sentenceItems(len, para, segOf),
			exposure: N,
		}),
		stat: pooledCv,
		extra: (_acc: DispAcc) => ({
			features: { cvWhole, meanSentenceLength: meanAll },
			summary: N ? [`shortest ${mn}w / longest ${mx}w`, `mean ${fmt(meanAll, 1)}w`] : [],
		}),
	});
}

export const rh01: ChannelImpl = { def: rh01Def, phase: 'derived', run };
